import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { USER_PERMISSIONS } from '../../utils/apiEndpoints';
import clientService from '../../apiClient';
import { mapUserPermission } from '../../utils/common';
import { Notification } from '../../components/Notification';
import { genWorkspaceHeader } from '../../utils/constants';

const initialState = {
  permissionsLoading: false,
  specificWorkspacePermissionMap: {},
  userPermissions: {},
};

export const permissionSlice = createSlice({
  name: 'permission',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchspecificWorkspacePermission.fulfilled, (state, action) => {
      state.specificWorkspacePermissionMap = mapUserPermission(action?.payload);
      state.permissionsLoading = false;
    });
    builder.addCase(fetchspecificWorkspacePermission.rejected, (state, action) => {
      state.specificWorkspacePermissionMap = {};
      state.permissionsLoading = false;
      Notification.error('Unable to fetch permissions', action?.payload.error);
    });
    builder.addCase(fetchspecificWorkspacePermission.pending, (state) => {
      state.permissionsLoading = true;
    });
    builder.addCase(addPermission.rejected, (state, action) => {
      state.permissionsLoading = false;

      Notification.error('Error adding permission');
    });
    builder.addCase(addPermission.pending, (state, action) => {
      state.permissionsLoading = true;
    });
    builder.addCase(addPermission.fulfilled, (state, action) => {
      state.permissionsLoading = false;

      const data = action?.payload?.data;
      const newObj = {
        is_organization_admin: data?.is_organization_admin,
        workspace_permission: data?.permission,
        file_permission: null, //need to add at later stage
        email: action?.payload?.email,
        username: data?.user_id,
      };
      state.specificWorkspacePermissionMap[action?.payload?.email] = newObj;
    });
    builder.addCase(updatePermission.fulfilled, (state, action) => {
      state.permissionsLoading = false;

      const email = action.payload.email;
      const newWorkspacePermission = action?.payload?.data?.permission;

      if (state.specificWorkspacePermissionMap[email]) {
        state.specificWorkspacePermissionMap[email].workspace_permission = newWorkspacePermission;
      }
    });
    builder.addCase(updatePermission.pending, (state) => {
      state.permissionsLoading = true;
    });
    builder.addCase(updatePermission.rejected, (state, action) => {
      state.permissionsLoading = false;
      Notification.error('Error updating Permission');
    });
    builder.addCase(deletePermission.pending, (state) => {
      state.permissionsLoading = true;
    });
    builder.addCase(deletePermission.fulfilled, (state, action) => {
      const email = action.payload;
      if (state.specificWorkspacePermissionMap[email]) {
        delete state.specificWorkspacePermissionMap[email];
      }
    });
    builder.addCase(deletePermission.rejected, (state, action) => {
      state.permissionsLoading = false;

      Notification.error('Error deleting Permission');
    });
  },
});

export const fetchspecificWorkspacePermission = createAsyncThunk(
  'permission/fetchspecificworkspacepermission',
  async (params, { rejectWithValue }) => {
    try {
      const response = await clientService.get(
        `${USER_PERMISSIONS}/workspaces`,
        { params },
        {
          headers: genWorkspaceHeader(params?.workspace_id),
        }
      );
      return response?.data;
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const addPermission = createAsyncThunk(
  'permission/addpermission',
  async ({ body, email }, { rejectWithValue }) => {
    try {
      const response = await clientService.post(`${USER_PERMISSIONS}`, body, {
        headers: genWorkspaceHeader(body?.workspace_id),
      });

      return { data: response?.data, email };
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const updatePermission = createAsyncThunk(
  'permission/updatepermission',
  async ({ body, params, email }, { rejectWithValue }) => {
    try {
      const response = await clientService.put(`${USER_PERMISSIONS}`, body, {
        params,
        headers: genWorkspaceHeader(body?.workspace_id),
      });

      return { data: response?.data, email };
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const deletePermission = createAsyncThunk(
  'permission/deletepermission',
  async ({ params, email, rejectWithValue }) => {
    try {
      const response = await clientService.delete(`${USER_PERMISSIONS}`, {
        params,
        headers: genWorkspaceHeader(params?.workspace_id),
      });

      return email;
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export default permissionSlice.reducer;
